import { Link, useLocation } from 'react-router-dom';
import { MS_LOCALES, MS_PAGES, msPath, msPathInLocale, useMsCopy, useMsLocale, type MsLegalDoc } from '@/membersheep/i18n';
import { MsHead } from './MsHead';

const ACCENT_LINK = 'text-ms-accent underline-offset-4 hover:underline dark:text-ms-blue';

interface DocArticleProps {
  doc: MsLegalDoc;
  /** Shown under the title: the governing-language note. */
  note: string;
}

/** Title, summary and sections of a legal document, with a sticky contents list on desktop. */
const DocArticle = ({ doc, note }: DocArticleProps) => {
  const copy = useMsCopy();
  const locale = useMsLocale();
  const { pathname } = useLocation();
  const other = MS_LOCALES.find((l) => l !== locale) ?? 'en';

  return (
    <main className="mx-auto grid max-w-[1080px] gap-0 px-4 pt-8 md:grid-cols-[220px_minmax(0,68ch)] md:gap-16 md:px-6 md:pt-14">
      <nav aria-label={copy.legal.onThisPage} className="sticky top-16 hidden self-start text-sm md:block">
        {doc.sections.map((section) => (
          <a key={section.id} href={`#${section.id}`} className="block border-l-2 border-sys-separator py-1.5 pl-3.5 hover:border-ms-accent hover:text-current text-sys-secondary">
            {section.heading}
          </a>
        ))}
      </nav>
      <article>
        <Link to={msPath(locale, MS_PAGES.home)} className={`text-sm ${ACCENT_LINK}`}>
          MemberSheep
        </Link>
        <h1 className="mt-2.5 font-display text-[40px] leading-none font-extrabold tracking-[-0.04em] md:text-[52px]">{doc.title}</h1>
        <p className="mt-3.5 text-sm text-sys-secondary">
          {doc.updated} · {note}{' '}
          <Link to={msPathInLocale(pathname, other)} className={ACCENT_LINK}>
            {copy.legal.otherLanguage}
          </Link>
        </p>

        {doc.summary && (
          <div className="mt-9 rounded-2xl border border-ms-accent/25 bg-ms-accent/[0.06] px-6 py-[22px] dark:border-ms-blue/30 dark:bg-ms-blue/10">
            <strong className="font-display">{doc.summaryTitle}</strong>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {doc.summary.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {doc.sections.map((section) => (
          <section key={section.id} id={section.id}>
            <h2 className="mt-12 font-display text-2xl leading-tight font-bold tracking-[-0.02em]">{section.heading}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph} className="mt-3.5 leading-relaxed">
                {paragraph}
              </p>
            ))}
            {section.items && (
              <ul className="mt-3.5 list-disc space-y-2 pl-5 leading-relaxed">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
            {section.links?.map((link) => (
              <p key={link.href} className="mt-3.5 text-[15px]">
                <a href={link.href} target="_blank" rel="noopener noreferrer" className={ACCENT_LINK}>
                  {link.label} ↗
                </a>
              </p>
            ))}
          </section>
        ))}
      </article>
    </main>
  );
};

interface MsLegalPageProps {
  kind: 'privacy' | 'terms';
}

/** /membersheep/privacy and /membersheep/terms, in English or 繁中. */
const MsLegalPage = ({ kind }: MsLegalPageProps) => {
  const copy = useMsCopy();

  return (
    <>
      <MsHead page={MS_PAGES[kind]} {...copy.seo[kind]} />
      <DocArticle doc={copy[kind]} note={copy.legal.governs} />
    </>
  );
};

export default MsLegalPage;
